import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';

interface BoardPost {
  id: string;
  title: string;
  content: string;
  category: string;
  categoryName?: string;
  isImportant: boolean;
  isPinned: boolean;
  requiredReadDeadline?: string;
  scheduledPublishDate?: string;
  attachments?: Array<{ id: string; name: string; url: string }>;
  surveyId?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  likesCount: number;
  commentsCount: number;
  isLiked: boolean;
  isRead: boolean;
}

interface Comment {
  id: string;
  content: string;
  userId: string;
  userName: string;
  createdAt: string;
}

interface Like {
  id: string;
  userId: string;
  userName: string;
}

interface Category {
  id: string;
  name: string;
}

export default function BoardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<BoardPost | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [likes, setLikes] = useState<Like[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showDeletePostConfirm, setShowDeletePostConfirm] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);

  useEffect(() => {
    fetchPost();
    fetchComments();
    fetchLikes();
    fetchCategories();
    markAsRead();
  }, [id]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const data = await api.get<BoardPost>(`/api/board/posts/${id}`);
      setPost(data);
    } catch {
      navigate('/board');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const data = await api.get<{ items: Comment[] }>(`/api/board/posts/${id}/comments`);
      setComments(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setComments([]);
    }
  };

  const fetchLikes = async () => {
    try {
      const data = await api.get<{ items: Like[] }>(`/api/board/posts/${id}/likes`);
      setLikes(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setLikes([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await api.get<{ items: Category[] }>('/api/board/categories');
      setCategories(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setCategories([]);
    }
  };

  const markAsRead = async () => {
    try {
      await api.post(`/api/board/posts/${id}/read`, {});
    } catch {
      // Silently fail
    }
  };

  const handleLike = async () => {
    if (!post) return;
    try {
      if (post.isLiked) {
        await api.delete(`/api/board/posts/${id}/like`);
        setPost({ ...post, isLiked: false, likesCount: post.likesCount - 1 });
        setLikes(likes.filter((l) => l.userId !== user?.id));
      } else {
        await api.post(`/api/board/posts/${id}/like`, {});
        setPost({ ...post, isLiked: true, likesCount: post.likesCount + 1 });
        setLikes([...likes, { id: 'temp', userId: user?.id || '', userName: user?.name || '' }]);
      }
    } catch {
      toast.error('いいねの更新に失敗しました');
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || sendingComment) return;

    setSendingComment(true);
    try {
      await api.post(`/api/board/posts/${id}/comments`, { content: newComment });
      setNewComment('');
      fetchComments();
      if (post) {
        setPost({ ...post, commentsCount: post.commentsCount + 1 });
      }
    } catch {
      toast.error('コメントの投稿に失敗しました');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setDeleteCommentId(commentId);
  };

  const confirmDeleteComment = async () => {
    if (!deleteCommentId) return;
    try {
      await api.delete(`/api/board/posts/${id}/comments/${deleteCommentId}`);
      fetchComments();
      if (post) {
        setPost({ ...post, commentsCount: post.commentsCount - 1 });
      }
      toast.success('コメントを削除しました');
    } catch {
      toast.error('コメントの削除に失敗しました');
    } finally {
      setDeleteCommentId(null);
    }
  };

  const handleDeletePost = () => {
    setShowDeletePostConfirm(true);
  };

  const confirmDeletePost = async () => {
    try {
      await api.delete(`/api/board/posts/${id}`);
      toast.success('投稿を削除しました');
      navigate('/board');
    } catch {
      toast.error('投稿の削除に失敗しました');
    } finally {
      setShowDeletePostConfirm(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'たった今';
    if (minutes < 60) return `${minutes}分前`;
    if (hours < 24) return `${hours}時間前`;
    if (days < 7) return `${days}日前`;
    return date.toLocaleDateString('ja-JP');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">投稿が見つかりません</p>
        <Link to="/board" className="text-blue-600 hover:underline mt-4 inline-block">
          一覧に戻る
        </Link>
      </div>
    );
  }

  const isAuthor = post.createdBy === user?.id;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/board')}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          掲示板に戻る
        </Button>
        {isAuthor && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowEditModal(true)}>
              編集
            </Button>
            <Button variant="danger" onClick={handleDeletePost}>
              削除
            </Button>
          </div>
        )}
      </div>

      {/* 投稿本文 */}
      <Card>
        <div className="space-y-4">
          {/* バッジ */}
          <div className="flex flex-wrap gap-2">
            {post.categoryName && (
              <Badge variant="default">{post.categoryName}</Badge>
            )}
            {post.isImportant && (
              <Badge variant="danger">重要</Badge>
            )}
            {post.isPinned && (
              <Badge variant="warning">📌 固定</Badge>
            )}
            {post.requiredReadDeadline && (
              <Badge variant="info">必読期限: {new Date(post.requiredReadDeadline).toLocaleDateString('ja-JP')}</Badge>
            )}
          </div>

          {/* タイトル */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{post.title}</h1>

          {/* 投稿者情報 */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">
              {post.createdByName?.charAt(0) || '?'}
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">{post.createdByName}</span>
              <span className="mx-2">·</span>
              <span>{formatDate(post.createdAt)}</span>
            </div>
          </div>

          {/* 本文 */}
          <div className="prose dark:prose-invert max-w-none">
            <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{post.content}</p>
          </div>

          {/* 添付ファイル */}
          {post.attachments && post.attachments.length > 0 && (
            <div className="border-t dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">添付ファイル</h4>
              <div className="space-y-2">
                {post.attachments.map((file) => (
                  <a
                    key={file.id}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {file.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* アクションバー */}
          <div className="flex items-center gap-4 pt-4 border-t dark:border-gray-700">
            <button
              onClick={handleLike}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                post.isLiked
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {post.isLiked ? '❤️' : '🤍'}
              <span className="font-medium">{post.likesCount}</span>
            </button>
            <button
              onClick={() => setShowLikesModal(true)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              いいねした人を見る
            </button>
            <span className="flex items-center gap-2 text-gray-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {post.commentsCount}
            </span>
          </div>
        </div>
      </Card>

      {/* コメントセクション */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          コメント ({comments.length})
        </h2>

        {/* コメント入力 */}
        <form onSubmit={handleAddComment} className="mb-6">
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="コメントを入力..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex justify-end mt-2">
                <Button type="submit" disabled={sendingComment || !newComment.trim()}>
                  {sendingComment ? '投稿中...' : 'コメントを投稿'}
                </Button>
              </div>
            </div>
          </div>
        </form>

        {/* コメント一覧 */}
        {comments.length === 0 ? (
          <p className="text-center text-gray-500 py-8">コメントはまだありません</p>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm flex-shrink-0">
                  {comment.userName?.charAt(0) || '?'}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                      {comment.userName}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatRelativeTime(comment.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {comment.content}
                  </p>
                  {comment.userId === user?.id && (
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-xs text-red-500 hover:text-red-700 mt-1"
                    >
                      削除
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* いいね一覧モーダル */}
      <Modal isOpen={showLikesModal} onClose={() => setShowLikesModal(false)} title="いいねした人">
        {likes.length === 0 ? (
          <p className="text-center text-gray-500 py-8">まだいいねがありません</p>
        ) : (
          <div className="space-y-3">
            {likes.map((like) => (
              <div key={like.id} className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">
                  {like.userName?.charAt(0) || '?'}
                </div>
                <span className="text-sm text-gray-900 dark:text-white">{like.userName}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="投稿を編集" size="lg">
        <EditPostForm
          post={post}
          categories={categories}
          onSave={() => {
            setShowEditModal(false);
            fetchPost();
          }}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* 投稿削除確認モーダル */}
      <Modal isOpen={showDeletePostConfirm} onClose={() => setShowDeletePostConfirm(false)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">この投稿を削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setShowDeletePostConfirm(false)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmDeletePost}>削除</Button>
        </div>
      </Modal>

      {/* コメント削除確認モーダル */}
      <Modal isOpen={!!deleteCommentId} onClose={() => setDeleteCommentId(null)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">このコメントを削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setDeleteCommentId(null)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmDeleteComment}>削除</Button>
        </div>
      </Modal>
    </div>
  );
}

// 編集フォームコンポーネント
function EditPostForm({
  post,
  categories,
  onSave,
  onCancel,
}: {
  post: BoardPost;
  categories: Category[];
  onSave: () => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    title: post.title,
    content: post.content,
    category: post.category,
    isImportant: post.isImportant,
    isPinned: post.isPinned,
    requiredReadDeadline: post.requiredReadDeadline || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.put(`/api/board/posts/${post.id}`, formData);
      onSave();
    } catch {
      toast.error('更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="タイトル"
        value={formData.title}
        onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">内容</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">カテゴリ</label>
          <Select
            value={formData.category}
            onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
          >
            <option value="">選択してください</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </Select>
        </div>
        <Input
          label="必読期限"
          type="date"
          value={formData.requiredReadDeadline}
          onChange={(e) => setFormData((prev) => ({ ...prev, requiredReadDeadline: e.target.value }))}
        />
      </div>
      <div className="flex gap-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isImportant}
            onChange={(e) => setFormData((prev) => ({ ...prev, isImportant: e.target.checked }))}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">重要</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isPinned}
            onChange={(e) => setFormData((prev) => ({ ...prev, isPinned: e.target.checked }))}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">固定表示</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
        <Button type="submit" loading={saving}>
          保存
        </Button>
      </div>
    </form>
  );
}

